import type { FacilityMeta, FacilityMetaContact } from './tier2submit-export.ts';
import type { TierIIReportFormEntry } from '../types/reporting.ts';
import type { TierIIChemicalCarrier, TierIIStateReporting } from '../../tri-shared/types/tierii-state-fields.ts';
import { isValidPhone } from './phone.ts';
import {
  chemicalStateFieldDefinitions,
  reportEntryKey,
  STATE_FIELDS_YEAR,
  stateFieldDefinitions,
} from './state-fields.ts';
import type { FieldApplicability } from './field-applicability.ts';
import { chemicalCarrierLabel, chemicalCarriersSelected, isChemicalCarrierField } from './chemical-carriers.ts';
import { addressValidation } from './address-validation.ts';

export function reportedBelowThreshold(
  meta: Pick<FacilityMeta, 'state' | 'stateReporting'>,
  entry: TierIIReportFormEntry,
): boolean {
  return (
    meta.stateReporting.supplemental[meta.state]?.chemicals[reportEntryKey(entry)]?.belowReportingThresholds ?? false
  );
}
export type StateFilingFee =
  | { status: 'not-required' }
  | { status: 'calculated'; amountDollars: number; explanation: string };

/** Only Hawaii's verified reporting-year rules require this XML field. */
export function stateFilingFee(
  meta: Pick<FacilityMeta, 'state' | 'reportingYear' | 'stateReporting'>,
  entries: TierIIReportFormEntry[],
): StateFilingFee {
  if (meta.state !== 'HI' || meta.reportingYear !== STATE_FIELDS_YEAR) return { status: 'not-required' };
  if (meta.stateReporting.facility.HI?.bool01 === true)
    return { status: 'calculated', amountDollars: 0, explanation: 'Federal government fee exemption applies.' };
  if (entries.length > 0 && entries.every((entry) => reportedBelowThreshold(meta, entry)))
    return {
      status: 'calculated',
      amountDollars: 0,
      explanation: 'All reported chemicals are marked below reporting thresholds.',
    };
  // Verified 2025 fee; this rule is gated by STATE_FIELDS_YEAR above.
  // https://health.hawaii.gov/heer/files/2026/01/Tier-II-packet-Reporting-Year-2025.pdf
  return {
    status: 'calculated',
    amountDollars: 200,
    explanation:
      'Hawaii filing fee for reporting year 2025. Included automatically in the report; payment is separate.',
  };
}

/** Vendor requirement for complete carrier contact details, separate from a chemical's carrier-name requirement. */
export function carrierRequired(
  state: string,
  year: number,
  entries: TierIIReportFormEntry[],
  reporting: TierIIStateReporting,
): boolean {
  return (
    year === STATE_FIELDS_YEAR &&
    (state === 'AK' ||
      (state === 'CT' &&
        entries.some((entry) => chemicalCarriersSelected(reporting, 'CT', reportEntryKey(entry)).length > 0)) ||
      (state === 'MA' && entries.some((entry) => entry.isEHS)))
  );
}
export function stateCarrierContacts(meta: FacilityMeta): FacilityMetaContact[] {
  return (meta.stateReporting.chemicalCarriers[meta.state] ?? []).map((carrier) => ({
    recordKey: 'carrier:' + meta.state + ':' + carrier.id,
    name: carrier.name,
    nameIsBusiness: true,
    title: null,
    email: null,
    parentCompanyDunAndBradstreet: null,
    mailingAddress: {
      street: carrier.street,
      city: carrier.city,
      state: carrier.state,
      zip: carrier.zip,
      country: carrier.country,
    },
    contactTypes: ['Chemical Carrier'],
    phones: [
      ...(carrier.phone.trim() ? [{ number: carrier.phone, type: 'Work' as const }] : []),
      ...(carrier.phone24.trim() ? [{ number: carrier.phone24, type: '24-hour' as const }] : []),
    ],
  }));
}

export function carrierApplicability(
  state: string,
  year: number,
  entries: TierIIReportFormEntry[],
  reporting: TierIIStateReporting,
): FieldApplicability {
  if (carrierRequired(state, year, entries, reporting)) return 'required';
  if (year !== STATE_FIELDS_YEAR) return 'not-applicable';
  // A required chemical Carrier answer is selected from this shared contact collection.
  if (
    entries.some((entry) =>
      chemicalStateFieldDefinitions(state, year, entry).some(
        (field) => isChemicalCarrierField(field) && field.required,
      ),
    )
  )
    return 'required';
  if (carrierRequested(state, year)) return 'requested';
  // Optional contacts are relevant where the state's shipment questions name a carrier.
  if (stateFieldDefinitions('chemical', state, year).some(isChemicalCarrierField)) return 'optional';
  return 'not-applicable';
}

export function carrierRequested(state: string, year: number): boolean {
  return year === STATE_FIELDS_YEAR && ['NH', 'RI', 'VT'].includes(state);
}
export function stateSupplementalIssues(meta: FacilityMeta, entries: TierIIReportFormEntry[]): string[] {
  const issues: string[] = [];
  for (const entry of entries) {
    const below = reportedBelowThreshold(meta, entry);
    const label = entry.basis === 'compound' ? entry.chemicalName : entry.productName;
    if (
      below &&
      entry.basis === 'compound' &&
      entry.maxAmountLbs !== null &&
      entry.maxAmountLbs >= entry.tier2ThresholdLbs
    )
      issues.push(label + ': cannot mark below reporting thresholds when the reported maximum reaches its threshold.');
    if (meta.state === 'OH' && meta.reportingYear === STATE_FIELDS_YEAR) {
      const feeClass = meta.stateReporting.chemicals.OH?.[reportEntryKey(entry)]?.text04;
      if (feeClass) {
        const expected = below
          ? 'Voluntary (below threshold)'
          : entry.isEHS
            ? 'Extremely Hazardous Substance (EHS)'
            : 'Hazardous Chemical';
        if (feeClass !== expected)
          issues.push(label + ': Ohio fee classification must be ' + expected + ' for this entry.');
      }
    }
  }
  const carriers = meta.stateReporting.chemicalCarriers[meta.state] ?? [];
  if (
    carrierApplicability(meta.state, meta.reportingYear, entries, meta.stateReporting) === 'required' &&
    carriers.length === 0
  )
    issues.push(
      'Add a Chemical Carrier contact in State Reporting for each company transporting the reported chemicals.',
    );
  if (new Set(carriers.map((carrier) => carrier.id)).size !== carriers.length)
    issues.push('Chemical Carrier contact identifiers must be unique. Remove and re-add the duplicate contact.');
  for (const [index, carrier] of carriers.entries()) {
    issues.push(
      ...carrierValidation(
        carrier,
        meta.state,
        carrierRequired(meta.state, meta.reportingYear, entries, meta.stateReporting),
        index,
      ).errors,
    );
  }
  return issues;
}

export function carrierValidation(carrier: TierIIChemicalCarrier, state: string, required: boolean, index: number) {
  const label = chemicalCarrierLabel(carrier, index);
  const address = addressValidation(carrier, label + ' mailing address', required);
  const fieldIssues = {
    ...address.fieldIssues,
    name: { errors: [] as string[], warnings: [] as string[] },
    phone: { errors: [] as string[], warnings: [] as string[] },
    phone24: { errors: [] as string[], warnings: [] as string[] },
  };
  if (!carrier.name.trim()) fieldIssues.name.errors.push('Enter company name.');
  if ((required || state === 'ME' || carrier.phone24.trim()) && !isValidPhone(carrier.phone24))
    fieldIssues.phone24.errors.push('Enter a valid 24-hour phone number.');
  if ((required || carrier.phone.trim()) && !isValidPhone(carrier.phone))
    fieldIssues.phone.errors.push('Enter a valid daytime phone number.');
  return {
    fieldIssues,
    errors: [
      ...(!carrier.name.trim() ? [label + ': company name is required.'] : []),
      ...(fieldIssues.phone24.errors.length ? [label + ': a valid 24-hour phone is required.'] : []),
      ...(fieldIssues.phone.errors.length ? [label + ': a valid daytime phone is required.'] : []),
      ...address.errors,
    ],
    warnings: address.warnings,
  };
}
